import { Popover } from './atoms/Popover';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { Select } from 'foundations-components/transitional/components/Select';
import { Button } from 'foundations-components/transitional/components/Button';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { useTicketListingsSortAndFilters } from '../hooks/useTicketListingsSortAndFilters';
import { useHasCompanyPermissions } from '../hooks/useHasCompanyPermissions';
import { useMemo, useState } from 'react';
import { generateStatusFilterOptions } from '../constants/filters';
import { UserTicketViewOption } from '../data/types';
import { generateDateFilterOptions } from '../constants/filters';
import { useHasConfiguredColumn } from '../utils/useHasConfiguredColumn';
import { useConfiguredColumnLabelOverride } from '../utils/useConfiguredColumnLabelOverride';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { AdvancedFiltersIcon } from 'foundations-assets/icons/components/AdvancedFiltersIcon';
import I18n from 'I18n';
import { useIsUngated, PortalMessage } from 'portals-platform-lib';
import { useIsMobile } from '../hooks/useBreakpoint';
import { MobilePopover } from './MobilePopover';
import { GATES } from '../constants/gates';
import { usageTracker } from '../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FilterLabel = ({
  label,
  htmlFor
}) => {
  return /*#__PURE__*/_jsx(FormLabel, {
    htmlFor: htmlFor,
    paddingBlockStart: "0",
    children: /*#__PURE__*/_jsx(Text, {
      typography: "heading-50",
      color: "text-core-default",
      children: label
    })
  });
};
export const FilterPopoverContent = ({
  showOwnershipFilter = false,
  isAdvancedSearchUngated,
  inlineSize,
  maxInlineSize,
  onClose
}) => {
  const {
    statusFilter,
    setStatusFilter,
    ticketView,
    setTicketView,
    createDateFilter,
    setCreateDateFilter,
    lastActivityFilter,
    setLastActivityFilter,
    conversationSearch,
    setConversationSearch,
    resetFilters
  } = useTicketListingsSortAndFilters();
  const isMobile = useIsMobile();
  const hasCompanyPermissions = useHasCompanyPermissions();
  const [localConversationSearch, setLocalConversationSearch] = useState(conversationSearch);
  const [prevConversationSearch, setPrevConversationSearch] = useState(conversationSearch);
  if (conversationSearch !== prevConversationSearch) {
    setPrevConversationSearch(conversationSearch);
    setLocalConversationSearch(conversationSearch);
  }
  const createdateFilterOptions = generateDateFilterOptions();
  const lastTicketActivityFilterOptions = generateDateFilterOptions();
  const hasCreatedateColumn = useHasConfiguredColumn('createdate');
  const hasLastTicketActivityColumn = useHasConfiguredColumn('hs_lastactivitydate');
  const createdateOverride = useConfiguredColumnLabelOverride('createdate');
  const lastActivityOverride = useConfiguredColumnLabelOverride('hs_lastactivitydate');
  const statusOverride = useConfiguredColumnLabelOverride('status');
  const createdateFilterLabel = /*#__PURE__*/_jsx(PortalMessage, {
    messageKey: "SUPPORT_SPACE_LISTINGS_PAGE_TICKET_PROPERTIES",
    subKey: "createdate",
    fallback: createdateOverride || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customer-portal-app.property.createdate"
    })
  });
  const lastTicketActivityFilterLabel = /*#__PURE__*/_jsx(PortalMessage, {
    messageKey: "SUPPORT_SPACE_LISTINGS_PAGE_TICKET_PROPERTIES",
    subKey: "hs_lastactivitydate",
    fallback: lastActivityOverride || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customer-portal-app.ticketListings.tableHeader.lastActivity"
    })
  });
  const statusFilterOptions = useMemo(() => generateStatusFilterOptions(), []);
  const statusFilterLabel = /*#__PURE__*/_jsx(PortalMessage, {
    messageKey: "SUPPORT_SPACE_LISTINGS_PAGE_TICKET_PROPERTIES",
    subKey: "status",
    fallback: statusOverride || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customer-portal-app.filters.ticketStatus"
    })
  });
  return /*#__PURE__*/_jsxs(Flex, {
    maxInlineSize: isMobile ? '100%' : maxInlineSize !== null && maxInlineSize !== void 0 ? maxInlineSize : '500px',
    inlineSize: isMobile ? '100%' : inlineSize !== null && inlineSize !== void 0 ? inlineSize : 'min(500px, 90vw)',
    flexDirection: "column",
    gap: "600",
    padding: "1000",
    children: [/*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      children: [/*#__PURE__*/_jsx(Text, {
        typography: "heading-400",
        color: "text-core-default",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.filters.searchOptions"
        })
      }), /*#__PURE__*/_jsx(Text, {
        typography: "body-100",
        color: "text-core-subtle",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.filters.searchOptionsDescription"
        })
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      children: [/*#__PURE__*/_jsx(FilterLabel, {
        htmlFor: "statusFilter",
        label: statusFilterLabel
      }), /*#__PURE__*/_jsx(ToggleGroup, {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: "400",
        rowGap: "0",
        name: "statusFilter",
        id: "statusFilter",
        "data-test-id": "status-filter",
        children: statusFilterOptions.map(option => /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(RadioInput, {
            value: option.value,
            "aria-label": option.text,
            inline: true,
            checked: statusFilter === option.value,
            onChange: () => {
              usageTracker.track('Customer Portal App', 'Tickets Discovered', {
                action: 'tickets-filtered',
                screen: 'ticket-listings',
                status: option.value
              });
              setStatusFilter(option.value);
            },
            children: /*#__PURE__*/_jsx(Text, {
              color: "text-core-default",
              children: option.text
            })
          })
        }, option.value))
      })]
    }), showOwnershipFilter && hasCompanyPermissions && /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      children: [/*#__PURE__*/_jsx(FilterLabel, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.filters.ticketView"
        })
      }), /*#__PURE__*/_jsx(ToggleGroup, {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: "400",
        rowGap: "0",
        name: "ticketView",
        id: "ticketView",
        "data-test-id": "ticket-view-filter",
        children: [{
          text: I18n.text('customer-portal-app.view.my'),
          value: UserTicketViewOption.PERSONAL
        }, {
          text: I18n.text('customer-portal-app.view.all'),
          value: UserTicketViewOption.COMPANY
        }].map(option => /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(RadioInput, {
            value: option.value,
            "aria-label": option.text,
            inline: true,
            checked: ticketView === option.value,
            onChange: () => {
              usageTracker.track('Customer Portal App', 'Tickets Discovered', {
                action: 'ticket-ownership-view-toggled',
                screen: 'ticket-listings',
                view: option.value
              });
              setTicketView(option.value);
            },
            children: /*#__PURE__*/_jsx(Text, {
              color: "text-core-default",
              children: option.text
            })
          })
        }, option.value))
      })]
    }), hasCreatedateColumn && /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      children: [/*#__PURE__*/_jsx(FilterLabel, {
        htmlFor: "createdateFilter",
        label: createdateFilterLabel
      }), /*#__PURE__*/_jsx(Select, {
        id: "createdateFilter",
        "data-test-id": "createdate-filter",
        searchable: false,
        value: createDateFilter,
        onChange: e => {
          usageTracker.track('Customer Portal App', 'Tickets Discovered', {
            action: 'tickets-filtered',
            screen: 'ticket-listings',
            timeRange: e.target.value
          });
          setCreateDateFilter(e.target.value);
        },
        options: createdateFilterOptions
      })]
    }), hasLastTicketActivityColumn && /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      children: [/*#__PURE__*/_jsx(FilterLabel, {
        htmlFor: "lastTicketActivityFilter",
        label: lastTicketActivityFilterLabel
      }), /*#__PURE__*/_jsx(Select, {
        id: "lastTicketActivityFilter",
        "data-test-id": "last-activity-filter",
        searchable: false,
        value: lastActivityFilter,
        onChange: e => setLastActivityFilter(e.target.value),
        options: lastTicketActivityFilterOptions
      })]
    }), isAdvancedSearchUngated && /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      children: [/*#__PURE__*/_jsx(FilterLabel, {
        htmlFor: "conversationSearchInput",
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.filters.conversationSearch.heading"
        })
      }), /*#__PURE__*/_jsx(TextInput, {
        id: "conversationSearchInput",
        "data-test-id": "conversation-search-input",
        "aria-label": I18n.text('customer-portal-app.filters.conversationSearch.heading'),
        placeholder: unescapedText('customer-portal-app.filters.conversationSearch.placeholder'),
        value: localConversationSearch,
        onChange: e => {
          if (e.target.value === '') {
            setConversationSearch('');
          }
          setLocalConversationSearch(e.target.value);
        },
        onKeyDown: e => {
          if (e.key === 'Enter') {
            usageTracker.track('Customer Portal App', 'Tickets Discovered', {
              action: 'conversation-searched',
              screen: 'ticket-listings'
            });
            setConversationSearch(localConversationSearch);
          }
        }
      }), /*#__PURE__*/_jsx(Text, {
        typography: "body-100",
        color: "text-core-subtle",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.filters.conversationSearch.warning"
        })
      })]
    }), !isMobile && /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsx(Button, {
        use: "transparent",
        size: "md",
        onClick: () => {
          usageTracker.track('Customer Portal App', 'Tickets Discovered', {
            action: 'tickets-filter-cleared',
            screen: 'ticket-listings'
          });
          resetFilters();
          setLocalConversationSearch('');
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.filters.reset"
        })
      }), isAdvancedSearchUngated && /*#__PURE__*/_jsx(Button, {
        use: "primary",
        size: "md",
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.filters.search"
        })
      })]
    })]
  });
};
const FilterPopoverButton = ({
  filterCount,
  open,
  setOpen
}) => /*#__PURE__*/_jsxs(Button, {
  use: "transparent",
  "data-test-id": "filter-button",
  "aria-label": unescapedText('customer-portal-app.filters.buttonLabelWithCount', {
    count: filterCount
  }),
  "aria-expanded": open,
  onClick: () => {
    if (!open) {
      usageTracker.track('Customer Portal App', 'Tickets Discovered', {
        action: 'ticket-filter-discovered',
        screen: 'ticket-listings'
      });
    }
    setOpen(!open);
  },
  children: [/*#__PURE__*/_jsx(AdvancedFiltersIcon, {}), /*#__PURE__*/_jsxs("span", {
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "customer-portal-app.filterLabel"
    }), filterCount > 0 && ` (${filterCount})`]
  })]
});
export const FilterPopover = ({
  placement = 'top',
  showOwnershipFilter = false
} = {}) => {
  const {
    resetFilters,
    activeFilterCount: filterCount
  } = useTicketListingsSortAndFilters();
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const isAdvancedSearchUngated = useIsUngated(GATES.ADVANCED_SEARCH);
  if (isMobile) {
    return /*#__PURE__*/_jsx(MobilePopover, {
      title: I18n.text('customer-portal-app.filters.searchOptions'),
      open: open,
      onOpenChange: setOpen,
      content: /*#__PURE__*/_jsx(FilterPopoverContent, {
        showOwnershipFilter: showOwnershipFilter,
        isAdvancedSearchUngated: isAdvancedSearchUngated
      }),
      footer: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(Button, {
            use: "transparent",
            onClick: () => {
              usageTracker.track('Customer Portal App', 'Tickets Discovered', {
                action: 'tickets-filter-cleared',
                screen: 'ticket-listings'
              });
              resetFilters();
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customer-portal-app.filters.reset"
            })
          })
        }), isAdvancedSearchUngated && /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(Button, {
            use: "primary",
            onClick: () => setOpen(false),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customer-portal-app.filters.search"
            })
          })
        })]
      }),
      children: /*#__PURE__*/_jsx(FilterPopoverButton, {
        filterCount: filterCount,
        open: open,
        setOpen: setOpen
      })
    });
  }
  return /*#__PURE__*/_jsx(Popover, {
    arrowSize: "none",
    marginInlineEnd: "1000",
    open: open,
    placement: placement,
    content: /*#__PURE__*/_jsx(FilterPopoverContent, {
      showOwnershipFilter: showOwnershipFilter,
      isAdvancedSearchUngated: isAdvancedSearchUngated,
      onClose: () => setOpen(false)
    }),
    onOpenChange: e => setOpen(e.currentTarget.value),
    children: /*#__PURE__*/_jsx(FilterPopoverButton, {
      filterCount: filterCount,
      open: open,
      setOpen: setOpen
    })
  });
};